import React, { useState, useEffect, useCallback } from 'react';
import { crmAPI } from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { Clock, AlertTriangle, CheckCircle, Circle, Calendar, User } from 'lucide-react';

const TASK_FILTERS = [
  { value: 'all', label: 'All Tasks' },
  { value: 'my_tasks', label: 'My Tasks' },
  { value: 'due_today', label: 'Due Today' },
  { value: 'overdue', label: 'Overdue' },
];

const PRIORITIES = [
  { value: 'low', label: 'Low', color: 'bg-gray-100 text-gray-800' },
  { value: 'med', label: 'Medium', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'high', label: 'High', color: 'bg-red-100 text-red-800' },
];

/**
 * AdminTasks - Task list view with filtering
 * @param {function} onViewTask - Callback when viewing a task
 * @param {string} initialAccountId - Optional account ID to filter tasks by customer
 * @param {string} initialLeadId - Optional lead ID to filter tasks by lead
 * @param {string} initialOrderId - Optional order ID to filter tasks by order
 */
export default function AdminTasks({ 
  onViewTask, 
  initialAccountId = null,
  initialLeadId = null,
  initialOrderId = null
}) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('open');

  const fetchTasks = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      
      if (filter === 'my_tasks') params.my_tasks = true;
      else if (filter === 'due_today') params.due_today = true;
      else if (filter === 'overdue') params.overdue = true;
      
      if (statusFilter) params.status = statusFilter;
      
      // Apply parent filters (from "View All" on detail pages)
      if (initialAccountId) {
        params.account_id = initialAccountId;
      }
      if (initialLeadId) {
        params.lead_id = initialLeadId;
      }
      if (initialOrderId) {
        params.order_id = initialOrderId;
      }
      
      const data = await crmAPI.getTasks(params);
      setTasks(data.tasks || []);
    } catch (err) {
      console.error('Failed to fetch tasks:', err);
    } finally {
      setLoading(false);
    }
  }, [filter, statusFilter, initialAccountId, initialLeadId, initialOrderId]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const handleCompleteTask = async (taskId) => {
    try {
      await crmAPI.completeTask(taskId);
      fetchTasks();
    } catch (err) {
      console.error('Failed to complete task:', err);
    }
  };

  const getPriorityStyle = (priority) => {
    const found = PRIORITIES.find(p => p.value === priority);
    return found ? found.color : 'bg-gray-100 text-gray-800';
  };

  const isOverdue = (dueDate) => {
    return new Date(dueDate) < new Date();
  };

  const formatDueDate = (date) => {
    const d = new Date(date);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    if (d.toDateString() === today.toDateString()) {
      return `Today ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } else if (d.toDateString() === tomorrow.toDateString()) {
      return `Tomorrow ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    }
    return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getTaskStats = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    const open = tasks.filter(t => t.status === 'open').length;
    const overdue = tasks.filter(t => t.status === 'open' && new Date(t.due_at) < new Date()).length;
    const dueToday = tasks.filter(t => {
      const d = new Date(t.due_at);
      return t.status === 'open' && d >= today && d < tomorrow;
    }).length;
    
    return { open, overdue, dueToday };
  };

  const stats = getTaskStats();

  return (
    <div className="p-6" data-testid="admin-tasks-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Tasks</h1>
        <p className="text-gray-600">Manage your tasks and follow-ups</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-sm border p-4" data-testid="stat-open-tasks">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Circle className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.open}</p>
              <p className="text-sm text-gray-600">Open Tasks</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border p-4" data-testid="stat-overdue-tasks">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-red-600">{stats.overdue}</p>
              <p className="text-sm text-gray-600">Overdue</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border p-4" data-testid="stat-due-today">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Calendar className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.dueToday}</p>
              <p className="text-sm text-gray-600">Due Today</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
        <div className="flex items-center gap-4">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40" data-testid="task-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TASK_FILTERS.map(f => (
                <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="w-32" data-testid="status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="done">Done</SelectItem>
              <SelectItem value="canceled">Canceled</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Tasks List */}
      <div className="bg-white rounded-lg shadow-sm border" data-testid="tasks-list">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : tasks.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-300" />
            <p>No tasks found</p>
          </div>
        ) : (
          <div className="divide-y">
            {tasks.map((task) => (
              <div 
                key={task.id} 
                className={`p-4 hover:bg-gray-50 cursor-pointer ${task.status === 'done' ? 'opacity-60' : ''}`}
                data-testid={`task-item-${task.id}`}
                onClick={() => onViewTask?.(task.id)}
              >
                <div className="flex items-start gap-4">
                  {/* Checkbox */}
                  <div className="pt-1">
                    {task.status === 'open' ? (
                      <Checkbox 
                        checked={false}
                        onCheckedChange={(e) => { e?.stopPropagation?.(); handleCompleteTask(task.id); }}
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`complete-task-${task.id}`}
                      />
                    ) : (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    )}
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className={`font-medium ${task.status === 'done' ? 'line-through text-gray-500' : ''}`}>
                        {task.title}
                      </h3>
                      <Badge className={getPriorityStyle(task.priority)} variant="secondary">
                        {task.priority?.toUpperCase()}
                      </Badge>
                    </div>
                    
                    {task.description && (
                      <p className="text-sm text-gray-600 mb-2 line-clamp-2">{task.description}</p>
                    )}
                    
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <div className={`flex items-center gap-1 ${task.status === 'open' && isOverdue(task.due_at) ? 'text-red-600 font-medium' : ''}`}>
                        {task.status === 'open' && isOverdue(task.due_at) && (
                          <AlertTriangle className="w-4 h-4" />
                        )}
                        <Clock className="w-4 h-4" />
                        <span>{formatDueDate(task.due_at)}</span>
                      </div>
                      
                      {task.lead_id && (
                        <div className="flex items-center gap-1">
                          <User className="w-4 h-4" />
                          <span>Lead: {task.lead_id}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Status */}
                  <div>
                    {task.status === 'done' && (
                      <Badge className="bg-green-100 text-green-800">Completed</Badge>
                    )}
                    {task.status === 'canceled' && (
                      <Badge className="bg-gray-100 text-gray-800">Canceled</Badge>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
